/**
 * asnTableWidthUtils.ts
 * Width calculation utilities for the ASN Location Details table.
 * 
 * Provides a centralized source of truth for column width calculations,
 * ensuring consistent sizing across headers, data rows, and grand totals.
 */

import { GRAIN_COL_WIDTH, METRIC_COL_WIDTH } from '../styles/asnOnePagerTableStyles';

export interface TableWidths {
  /** Total width of the sticky (grain) columns */
  grainWidth: number;
  /** Width of each metric column (expands on wide screens, minimum on narrow) */
  metricWidth: number;
  /** Total width required for all metric columns (used for ScrollView contentContainerStyle) */
  totalMetricWidth: number;
}

/**
 * Calculate column widths based on available table width.
 * 
 * Distributes available space to metric columns while keeping grain columns fixed.
 * On wide viewports, metrics expand to fill space. On narrow viewports, metrics stay
 * at minimum width (METRIC_COL_WIDTH) and horizontal scrolling handles overflow.
 * 
 * @param tableWidth - Measured width of the table container (in pixels)
 * @param grainCount - Number of selected grain dimensions
 * @param metricCount - Number of metrics to display
 * @returns Object containing calculated widths for use throughout the table
 */
export const calculateTableWidths = (
  tableWidth: number,
  grainCount: number,
  metricCount: number,
): TableWidths => {
  // Calculate the width consumed by grain columns
  const grainWidth = grainCount * GRAIN_COL_WIDTH;
  
  // Calculate available width for metrics after grain columns
  const availableMetricWidth = Math.max(0, tableWidth - grainWidth);
  
  // Determine metric column width:
  // - On wide screens: metrics expand equally to fill available space
  // - On narrow screens: metrics stay at minimum METRIC_COL_WIDTH and trigger horizontal scroll
  const minimumMetricWidth = METRIC_COL_WIDTH;
  const calculatedMetricWidth = metricCount > 0
    ? availableMetricWidth / metricCount
    : minimumMetricWidth;
  
  const metricWidth = Math.max(minimumMetricWidth, calculatedMetricWidth);
  
  // Total metric area width used for ScrollView contentContainerStyle
  // This expands naturally when metrics expand, and allows horizontal scroll when needed
  const totalMetricWidth = metricWidth * metricCount;
  
  return {
    grainWidth,
    metricWidth,
    totalMetricWidth,
  };
};
